import React, { Fragment, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import {
  LOCAIS_POR_CIDADE,
  DISPONIBILIDADE_POR_LOCAL_NA_CIDADE,
  todayISO,
  formatCurrency,
} from '../../services/quadraService';
import { QUADRAS_POR_LOCAL } from '../../services/quadraAdminService';
import { useEffect } from 'react';
import { buscarDescontoAlunoParaUsuario } from '../../services/ligaLocalService';

const MEU_CREDITOS_BUSCA = gql`
  query MeusCreditosBusca {
    meusCreditosPorLocal {
      localId
      localNome
      cidadeNome
      saldo
    }
  }
`;

const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

// Card de local — mostra info básica e navega para LocalDetalhe ao clicar
// NOTA: Removido DISPONIBILIDADE_QUADRAS_BATCH para evitar sobrecarga do servidor.
// A disponibilidade será mostrada apenas na página de detalhe do local.
const LocalCard: React.FC<{ local: any; data: string; saldoCredito: number; slotsLivres?: number }> = ({ local, data, saldoCredito, slotsLivres }) => {
  const navigate = useNavigate();
  const [percentualDesconto, setPercentualDesconto] = useState<number | null>(null);

  useEffect(() => {
    buscarDescontoAlunoParaUsuario(local.id).then(setPercentualDesconto);
  }, [local.id]);

  const { data: quadrasData } = useQuery(QUADRAS_POR_LOCAL, {
    variables: { localId: local.id },
    fetchPolicy: 'cache-and-network',
  });

  const quadras: any[] = (quadrasData?.quadrasPorLocal ?? []).filter((q: any) => q.ativa);

  return (
    <div
      className="card shadow-xss rounded-xxl border-0 mb-3 p-4"
      style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
      onClick={() => navigate(`/quadras/local/${local.id}?data=${data}`)}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.13)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = '')}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <span className="btn-round-lg bg-primary-gradiant d-flex align-items-center justify-content-center" style={{ minWidth: 48, minHeight: 48 }}>
            <i className="bi bi-buildings text-white font-md"></i>
          </span>
          <div>
            <h4 className="fw-700 font-xss mb-0 text-grey-900">{local.nome}</h4>
            <span className="font-xssss text-grey-500">
              <i className="feather-map-pin me-1"></i>
              {local.endereco?.bairro}{local.endereco?.cidade?.nome ? `, ${local.endereco.cidade.nome}` : ''}
            </span>
            {local.descricao && (
              <span className="font-xssss text-grey-400 d-block">{local.descricao}</span>
            )}
            {percentualDesconto != null && percentualDesconto > 0 && (
              <span className="badge bg-success text-white font-xssss rounded-xl mt-1 d-inline-block">
                <i className="bi bi-percent me-1"></i>{percentualDesconto}% desconto aluno
              </span>
            )}
            {saldoCredito > 0 && (
              <div className="bg-greylight rounded-xl px-2 py-1 mt-1 d-inline-flex align-items-center gap-1">
                <i className="bi bi-wallet2 text-grey-600" style={{ fontSize: 11 }}></i>
                <span className="font-xssss text-grey-600">Crédito disponível</span>
                <span className="font-xssss fw-700 text-success">{formatCurrency(saldoCredito)}</span>
              </div>
            )}
            {quadras.length > 0 && (
              <span className="font-xssss text-grey-500 d-block mt-1">
                <i className="bi bi-grid-3x3 me-1"></i>
                {quadras.length} quadra{quadras.length !== 1 ? 's' : ''}
                {' · '}
                {quadras.map((q: any) => q.tipoPiso).filter(Boolean).filter((v: string, i: number, a: string[]) => a.indexOf(v) === i).join(', ')}
              </span>
            )}
            {slotsLivres != null && (
              <span
                className={`badge font-xssss rounded-xl mt-1 d-inline-block ${slotsLivres > 0 ? 'bg-success text-white' : 'bg-greylight text-grey-600'}`}
              >
                <i className="feather-clock me-1"></i>
                {slotsLivres > 0 ? `${slotsLivres} horário${slotsLivres !== 1 ? 's' : ''} livre${slotsLivres !== 1 ? 's' : ''}` : 'Sem horários livres'}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {local.urlLocalizacao && (
            <a
              href={local.urlLocalizacao}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-light rounded-xl font-xssss px-2 py-1"
              title="Ver no mapa"
              onClick={e => e.stopPropagation()}
            >
              <i className="feather-map-pin"></i>
            </a>
          )}
          <span className="btn btn-light rounded-xl font-xssss px-3 py-1 fw-600">
            Ver quadras <i className="feather-chevron-right ms-1"></i>
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────

const QuadrasDisponiveis: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cidadeId = searchParams.get('cidadeId') ?? '';
  const cidadeNome = searchParams.get('cidadeNome') ?? '';
  const navigate = useNavigate();

  const dataParam = searchParams.get('data') ?? todayISO();
  const [data, setData] = useState(dataParam);
  const [busca, setBusca] = useState('');
  const [criterio, setCriterio] = useState<'disponibilidade' | 'nome'>('disponibilidade');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('desc');

  const setDataNav = (novaData: string) => {
    if (novaData < todayISO()) return;
    setData(novaData);
    const p = new URLSearchParams(searchParams);
    p.set('data', novaData);
    setSearchParams(p, { replace: true });
  };

  const { data: locaisData, loading, error } = useQuery(LOCAIS_POR_CIDADE, {
    variables: { cidadeId, aluguelHabilitado: true },
    fetchPolicy: 'cache-and-network',
  });

  const locais: any[] = locaisData?.locais ?? [];

  // Disponibilidade (slots livres) por local na data — calculada em lote no backend.
  const { data: dispData } = useQuery(DISPONIBILIDADE_POR_LOCAL_NA_CIDADE, {
    variables: { cidadeId, data },
    skip: !cidadeId,
    fetchPolicy: 'cache-and-network',
  });
  const slotsLivresPorLocal: Record<string, number> = {};
  (dispData?.disponibilidadePorLocalNaCidade ?? []).forEach((d: any) => {
    slotsLivresPorLocal[d.localId] = d.slotsLivres;
  });

  // Filtro por nome da arena (ou bairro), aplicado em memória sobre a lista já carregada
  const termoBusca = busca.trim().toLowerCase();
  const locaisFiltradosSemOrdem = termoBusca
    ? locais.filter((l: any) =>
        (l.nome ?? '').toLowerCase().includes(termoBusca) ||
        (l.endereco?.bairro ?? '').toLowerCase().includes(termoBusca)
      )
    : locais;

  // Ordenação em dois eixos: critério (disponibilidade | nome) e direção (asc | desc).
  const fatorDirecao = direcao === 'asc' ? 1 : -1;
  const locaisFiltrados = [...locaisFiltradosSemOrdem].sort((a: any, b: any) => {
    if (criterio === 'nome') {
      return fatorDirecao * (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR', { sensitivity: 'base' });
    }
    // disponibilidade: por slots livres; empate desempata por nome (A–Z) para ordem estável.
    const sa = slotsLivresPorLocal[a.id] ?? 0;
    const sb = slotsLivresPorLocal[b.id] ?? 0;
    if (sa !== sb) return fatorDirecao * (sa - sb);
    return (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR', { sensitivity: 'base' });
  });

  const { data: creditosData } = useQuery(MEU_CREDITOS_BUSCA, { fetchPolicy: 'cache-first' });
  // Filtra créditos que pertencem a locais nesta cidade
  const creditosNaCidade = (creditosData?.meusCreditosPorLocal ?? []).filter((c: any) =>
    locais.some((l: any) => l.id === c.localId)
  );

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="card shadow-xss w-100 border-0 p-4 mb-3">
              <div className="d-flex align-items-center mb-3">
                <button className="btn btn-light btn-sm me-3 rounded-xl" onClick={() => navigate('/quadras')}>
                  <i className="feather-arrow-left me-1"></i> Cidades
                </button>
                <div>
                  <h2 className="fw-700 mb-0 font-md text-grey-900">
                    <i className="bi bi-geo-alt-fill text-primary me-2"></i>{cidadeNome}
                  </h2>
                  <p className="font-xssss text-grey-500 mb-0">Selecione um local para ver as quadras</p>
                </div>
              </div>

              <div className="d-flex align-items-center gap-1">
                <label className="font-xssss text-grey-500 fw-600 me-1">Data:</label>
                <button
                  type="button"
                  className="btn btn-light rounded-xl px-2"
                  style={{ height: 38 }}
                  onClick={() => setDataNav(addDays(data, -1))}
                  disabled={data <= todayISO()}
                ><i className="feather-chevron-left"></i></button>
                <input
                  type="date"
                  className="form-control rounded-xl font-xsss"
                  value={data}
                  min={todayISO()}
                  onChange={e => setDataNav(e.target.value || todayISO())}
                  style={{ width: 145, height: 38 }}
                />
                <button
                  type="button"
                  className="btn btn-light rounded-xl px-2"
                  style={{ height: 38 }}
                  onClick={() => setDataNav(addDays(data, 1))}
                ><i className="feather-chevron-right"></i></button>
                <span className="font-xssss fw-600 text-grey-600 ms-1">
                  {['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'][new Date(data + 'T12:00:00').getDay()]}
                </span>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <div className="position-relative flex-grow-1" style={{ minWidth: 200 }}>
                  <i className="feather-search position-absolute text-grey-400" style={{ left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 15 }}></i>
                  <input
                    type="text"
                    className="form-control rounded-xl font-xsss w-100"
                    placeholder="Buscar arena pelo nome..."
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    style={{ paddingLeft: 38, paddingRight: busca ? 38 : 14, height: 42 }}
                  />
                  {busca && (
                    <button
                      type="button"
                      className="btn btn-link position-absolute p-0 text-grey-500"
                      style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }}
                      onClick={() => setBusca('')}
                      title="Limpar busca"
                    ><i className="feather-x"></i></button>
                  )}
                </div>
                <div className="d-flex align-items-center gap-1">
                  <label className="font-xssss text-grey-500 fw-600 me-1 mb-0"><i className="feather-sliders me-1"></i>Ordenar:</label>
                  <select
                    className="form-select rounded-xl font-xsss"
                    value={criterio}
                    onChange={e => setCriterio(e.target.value as 'disponibilidade' | 'nome')}
                    style={{ width: 165, height: 42 }}
                  >
                    <option value="disponibilidade">Disponibilidade</option>
                    <option value="nome">Nome</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-light rounded-xl px-2"
                    style={{ height: 42 }}
                    onClick={() => setDirecao(d => (d === 'asc' ? 'desc' : 'asc'))}
                    title={
                      criterio === 'nome'
                        ? (direcao === 'asc' ? 'A → Z (crescente)' : 'Z → A (decrescente)')
                        : (direcao === 'asc' ? 'Menor disponibilidade primeiro' : 'Maior disponibilidade primeiro')
                    }
                  >
                    <i className={`feather-arrow-${direcao === 'asc' ? 'up' : 'down'}`}></i>
                    <span className="font-xssss fw-600 ms-1">{direcao === 'asc' ? 'Crescente' : 'Decrescente'}</span>
                  </button>
                </div>
              </div>
            </div>

            {loading && <Load />}
            {error && <div className="alert alert-danger">{error.message}</div>}

            {!loading && !error && locais.length === 0 && (
              <div className="card shadow-xss w-100 p-5 text-center border-0">
                <i className="bi bi-buildings font-xxl text-grey-300 d-block mb-2"></i>
                <p className="text-grey-500 font-xss mb-0">Nenhum local disponível nesta cidade.</p>
              </div>
            )}

            {!loading && !error && locais.length > 0 && locaisFiltrados.length === 0 && (
              <div className="card shadow-xss w-100 p-5 text-center border-0">
                <i className="feather-search font-xxl text-grey-300 d-block mb-2"></i>
                <p className="text-grey-500 font-xss mb-1">Nenhuma arena encontrada para "{busca.trim()}".</p>
                <button className="btn btn-link font-xsss p-0" onClick={() => setBusca('')}>Limpar busca</button>
              </div>
            )}

            {locaisFiltrados.map((local: any) => {
              const credito = creditosNaCidade.find((c: any) => c.localId === local.id);
              return <LocalCard key={local.id} local={local} data={data} saldoCredito={credito?.saldo ?? 0} slotsLivres={slotsLivresPorLocal[local.id]} />;
            })}

          </div>
        </div>
      </div>
      <Appfooter />
    </Fragment>
  );
};

export default QuadrasDisponiveis;
